import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, LoadingIndicator, Row } from "@/components";
import { FormTextInput } from "@/components/form/FormTextInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useCreateCompanyFile } from "@/hooks/mutations/useCreateCompanyFile";
import { useAddCompanyFile } from "@/hooks/sets/useAddCompanyFile";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CompanyFile } from "@/types";

const createCompanyFileSchema = z.object({
  companyFile: z.instanceof(File, {
    message: "Required",
  }),
  name: z.string().min(1, {
    message: "Required",
  }),
});

export const CreateCompanyFileDialog = ({
  onOpenChange,
  onSuccess,
  open,
}: {
  onOpenChange: (open: boolean) => void;
  onSuccess?: (newCompanyFile?: CompanyFile) => void;
  open: boolean;
}) => {
  const {
    mutate: createCompanyFile,
    isPending: isCreateCompanyFilePending,
    isError: isCreateCompanyFileError,
    reset: resetCreateCompanyFile,
  } = useCreateCompanyFile();
  const addCompanyFile = useAddCompanyFile();

  const form = useForm<z.infer<typeof createCompanyFileSchema>>({
    defaultValues: {
      name: "",
    },
    resolver: zodResolver(createCompanyFileSchema),
  });

  function onSubmit(data: z.infer<typeof createCompanyFileSchema>) {
    mixpanelUtils.trackButtonClick("Upload file", MIXPANEL_SCREENS.COMPANY);
    createCompanyFile(
      {
        createCompanyFileRequest: {
          companyFile: data.companyFile,
          name: data.name.trim(),
        },
      },
      {
        onSuccess: (newCompanyFile) => {
          form.reset();
          onSuccess?.(newCompanyFile);
          addCompanyFile({
            companyFileToAdd: newCompanyFile,
          });
        },
      },
    );
  }

  return (
    <Dialog
      onOpenChange={(open) => {
        // Reset the form and mutation state when the dialog is closed.
        form.reset();
        resetCreateCompanyFile();
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Upload File
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <Column className="gap-4">
              <FormTextInput
                placeholder="The name of the file minus the extension."
                fieldName="name"
                form={form}
                label="Name"
              />
              <FormField
                control={form.control}
                name="companyFile"
                render={({ field: { onChange, ...field } }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>File</FormLabel>
                    <FormControl>
                      <Input
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          onChange(file ?? undefined);
                        }}
                        type="file"
                        {...field}
                        // QUESTION: Why is this needed?
                        value={undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Column>
            <Column className="gap-2">
              <Row className="justify-end">
                <Button
                  className="relative rounded-full"
                  disabled={isCreateCompanyFilePending}
                  type="submit"
                >
                  <span
                    className={isCreateCompanyFilePending ? "invisible" : ""}
                  >
                    Upload
                  </span>
                  {isCreateCompanyFilePending && (
                    <LoadingIndicator
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                      gearColor="white"
                      variant="small"
                    />
                  )}
                </Button>
              </Row>
              {isCreateCompanyFileError && (
                <ErrorText errorMessage="An error occurred while uploading file. Please try again." />
              )}
            </Column>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
